import { FormEvent, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Link, Stack, TextField, Typography } from '@mui/material';
import { ApiError } from '@/auth/api';
import { safeNextPath, withNext } from '@/auth/nextPath';
import {
  emptyRegistration,
  passwordConfirmationError,
  validateRegistration,
  type RegistrationFieldErrors,
} from '@/auth/registration';
import type { RegisterCustomerFormValues } from '@/auth/types';
import { useAuth } from '@/auth/useAuth';
import { NarrowPage } from '@/components/NarrowPage';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';

export function CustomerRegisterPage() {
  const navigate = useNavigate();
  const { registerCustomer } = useAuth();
  const [searchParams] = useSearchParams();
  const next = safeNextPath(searchParams.get('next'));
  const [values, setValues] = useState(emptyRegistration);
  const [errors, setErrors] = useState<RegistrationFieldErrors>({});
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [confirmationError, setConfirmationError] = useState<string | undefined>();
  const [requestError, setRequestError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setField = (field: keyof RegisterCustomerFormValues, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validateRegistration(values);
    const nextConfirmationError = passwordConfirmationError(values.password, passwordConfirmation);
    setErrors(nextErrors);
    setConfirmationError(nextConfirmationError);
    setRequestError(null);

    if (Object.keys(nextErrors).length || nextConfirmationError) return;

    setIsSubmitting(true);
    try {
      const result = await registerCustomer(values);

      // A new account is signed in at once (ADR 0008): it goes back to the page that sent it, or
      // is told to verify the email. Claiming a counter account always goes to the mail.
      if (result.session && next) {
        navigate(next, { replace: true });
        return;
      }

      navigate('/account/check-email', { replace: true, state: { email: result.email, next } });
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        setRequestError(
          'Alcanzaste el límite de intentos. Esperá unos minutos y volvé a intentar.',
        );
      } else if (error instanceof ApiError && error.status === 409) {
        setRequestError('No pudimos crear la cuenta con esos datos.');
      } else if (error instanceof ApiError && error.status === 400) {
        setRequestError('Revisá los datos ingresados y volvé a intentar.');
      } else {
        setRequestError('No pudimos crear la cuenta. Volvé a intentar.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <NarrowPage maxWidth={600}>
      <PageHeader
        eyebrow="CUENTA DE COMPRADOR"
        title="Creá tu cuenta"
        description="Registrate para identificarte como comprador y prepararte para futuras reservas y compras."
      />
      <SectionCard title="Tus datos" description="Todos los campos son obligatorios.">
        <Stack component="form" spacing={2} onSubmit={handleSubmit} noValidate>
          {requestError ? <Alert severity="error">{requestError}</Alert> : null}
          <TextField
            label="Nombre y apellido"
            autoComplete="name"
            value={values.fullName}
            onChange={(event) => setField('fullName', event.target.value)}
            error={Boolean(errors.fullName)}
            helperText={errors.fullName}
            fullWidth
          />
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            value={values.email}
            onChange={(event) => setField('email', event.target.value)}
            error={Boolean(errors.email)}
            helperText={errors.email}
            fullWidth
          />
          <TextField
            label="Teléfono"
            type="tel"
            autoComplete="tel"
            placeholder="099 123 456"
            value={values.phone}
            onChange={(event) => setField('phone', event.target.value)}
            error={Boolean(errors.phone)}
            helperText={
              errors.phone || 'Podés escribir un número uruguayo o incluir el código de país.'
            }
            fullWidth
          />
          <TextField
            label="Contraseña"
            type="password"
            autoComplete="new-password"
            value={values.password}
            onChange={(event) => setField('password', event.target.value)}
            error={Boolean(errors.password)}
            helperText={
              errors.password ||
              'Mínimo 10 caracteres, con una mayúscula, un número y un carácter especial.'
            }
            fullWidth
          />
          <TextField
            label="Repetir contraseña"
            type="password"
            autoComplete="new-password"
            value={passwordConfirmation}
            onChange={(event) => {
              setPasswordConfirmation(event.target.value);
              setConfirmationError(undefined);
            }}
            error={Boolean(confirmationError)}
            helperText={confirmationError}
            fullWidth
          />
          <Alert severity="info">
            Usaremos tu nombre, email y teléfono únicamente para gestionar tu cuenta, tus reservas y
            tus compras, y para contactarte sobre ellas.
          </Alert>
          <PrimaryButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creando cuenta...' : 'Crear cuenta'}
          </PrimaryButton>
          <Typography variant="body2" color="text.secondary">
            ¿Ya tenés una cuenta?{' '}
            <Link component={RouterLink} to={withNext('/login', next)} underline="hover">
              Ingresá
            </Link>
          </Typography>
        </Stack>
      </SectionCard>
    </NarrowPage>
  );
}
